/**
 * 사업자 선택기 — 실측 146곳에서 **하나**를 고른다.
 *
 * **드롭다운 + 검색인 이유.** 146개를 칩으로 펼치면 화면 절반을 상시 점유하고,
 * 순수 `<select>` 로 두면 이름 첫 글자를 알아야만 찾을 수 있다 (코드가 `PI`·`PW`
 * 두 글자라 알파벳 순서도 도움이 안 된다). 필요할 때만 열리고 검색으로 좁히는
 * 문법은 상단 필터 바(§8.6.1)가 이미 쓰는 것이므로 그 팝오버 문법(`.pop*`)을
 * 그대로 재사용한다 — 같은 화면에서 두 종류의 선택기가 다르게 생기면 안 된다.
 *
 * **필터 바의 사업자 필터와는 다른 것이다.** 필터 바는 "어떤 사업자들을 집계에
 * 포함할까"(다중)이고, 여기는 "누구의 스코어카드를 볼까"(단일)다. 그래서 전역
 * 필터 상태에 섞지 않고 페이지가 지역 상태로 들고 있는다.
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { fetchPlain } from '@/lib/api'
import { int } from '@/lib/fmt'
import './OperatorPicker.css'

interface Option {
  code: string
  label: string
  n: number | null
}

interface FilterOptions {
  options: Record<string, Option[]>
  data_version: string
}

interface Props {
  value: string | null
  onChange: (code: string) => void
}

export function OperatorPicker({ value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const boxRef = useRef<HTMLDivElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  // 필터 바와 같은 쿼리 키를 쓴다 — 사업자 목록을 두 번 받아 올 이유가 없다
  const { data, isLoading } = useQuery({
    queryKey: ['meta', 'filters'],
    queryFn: ({ signal }) => fetchPlain<FilterOptions>('/meta/filters', signal),
    staleTime: 5 * 60 * 1000,
  })

  const options = useMemo(() => data?.options.busi ?? [], [data])

  /**
   * 목록이 도착하면 **충전기 최다 사업자**를 자동으로 고른다.
   *
   * 아무것도 고르지 않은 빈 화면으로 시작하면 "이 페이지가 무엇을 보여주는
   * 곳인지"를 한 번 클릭한 뒤에야 알 수 있다. `/meta/filters` 의 busi 는 이미
   * 충전기 수 내림차순이므로 첫 항목이 곧 가장 큰 사업자다.
   */
  useEffect(() => {
    const first = options[0]
    if (value == null && first) onChange(first.code)
  }, [value, options, onChange])

  // 바깥 클릭·Escape 로 닫는다 — 포커스가 갇히지 않게 (필터 바와 같은 규칙)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // 열면 검색창으로 바로 간다 — 146곳을 스크롤로 찾게 두지 않는다
  useEffect(() => {
    if (open) searchRef.current?.focus()
  }, [open])

  const current = options.find((o) => o.code === value) ?? null
  const q = term.trim()
  const list = q ? options.filter((o) => o.label.includes(q) || o.code.includes(q)) : options

  return (
    <div className="opick" ref={boxRef}>
      <button
        type="button"
        className="opick__trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={options.length === 0}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="opick__triggerlabel">사업자 선택</span>
        <span className="opick__triggername">
          {current?.label ?? (isLoading ? '불러오는 중…' : '없음')}
        </span>
        <span className="opick__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="pop opick__pop" role="dialog" aria-label="사업자 선택">
          <div className="pop__head">
            <input
              ref={searchRef}
              type="search"
              className="pop__search"
              placeholder={`사업자 검색 (${options.length}곳)`}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              aria-label="사업자 검색"
            />
          </div>
          <ul className="pop__list">
            {list.map((o) => (
              <li key={o.code}>
                <button
                  type="button"
                  className={`opick__opt${o.code === value ? ' is-on' : ''}`}
                  aria-pressed={o.code === value}
                  onClick={() => {
                    onChange(o.code)
                    setOpen(false)
                  }}
                >
                  <span className="pop__optlabel">{o.label}</span>
                  {/* 코드를 함께 보여준다 — 이름이 비슷한 지자체가 여럿이다 */}
                  <span className="opick__optcode mono">{o.code}</span>
                  {o.n != null && <span className="pop__optn num">{int(o.n)}기</span>}
                </button>
              </li>
            ))}
            {list.length === 0 && <li className="pop__empty">검색 결과가 없습니다.</li>}
          </ul>
        </div>
      )}
    </div>
  )
}
